import { useState } from 'react';
import { Globe, Lock, Eye, EyeOff, Link, Calendar, Users } from 'lucide-react';

export default function PropertyLinkSettings() {
  const [settings, setSettings] = useState({
    enabled: true,
    allowDirectBooking: true,
    showAvailability: true,
    showPricing: true,
    requireLogin: false,
    customSlug: 'santafe-apartamento',
    visibility: 'public' as 'public' | 'unlisted' | 'private',
  });

  const [stats] = useState({
    views: 127,
    clicks: 34,
    bookings: 3,
    conversionRate: '8.8%',
  });

  const fullUrl = `vecindus.com/rental/${settings.customSlug}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Enlace Público de Propiedad</h2>
        <p className="text-slate-400">Comparte tu propiedad y recibe reservas directas</p>
      </div>

      {/* Toggle */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${settings.enabled ? 'bg-green-500/20' : 'bg-slate-700'}`}>
              <Globe className={`w-6 h-6 ${settings.enabled ? 'text-green-400' : 'text-slate-500'}`} />
            </div>
            <div>
              <h3 className="text-white font-semibold">Página Pública</h3>
              <p className="text-slate-400 text-sm">
                {settings.enabled ? 'Tu propiedad es visible públicamente' : 'Tu propiedad está oculta'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setSettings({...settings, enabled: !settings.enabled})}
            className={`w-14 h-8 rounded-full transition ${settings.enabled ? 'bg-green-500' : 'bg-slate-600'}`}
          >
            <div className={`w-6 h-6 bg-white rounded-full transition ${settings.enabled ? 'translate-x-7' : 'translate-x-1'} mt-1`} />
          </button>
        </div>
      </div>

      {/* URL Preview */}
      {settings.enabled && (
        <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-5 rounded-xl border border-blue-500/30">
          <label className="block text-slate-400 text-sm mb-2">URL de tu propiedad</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={fullUrl}
              readOnly
              className="flex-1 bg-[var(--v-surface-1)] text-white px-4 py-3 rounded-lg border border-[var(--v-surface-3)]"
            />
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium">
              Copiar
            </button>
          </div>
          <p className="text-slate-400 text-sm mt-2">
            <Link className="w-4 h-4 inline mr-1" />
            Comparte este enlace en redes sociales, WhatsApp, o tu sitio web
          </p>
        </div>
      )}

      {/* Stats */}
      {settings.enabled && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] text-center">
            <p className="text-3xl font-bold text-white">{stats.views}</p>
            <p className="text-slate-400 text-sm">Vistas</p>
          </div>
          <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] text-center">
            <p className="text-3xl font-bold text-blue-400">{stats.clicks}</p>
            <p className="text-slate-400 text-sm">Clics</p>
          </div>
          <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] text-center">
            <p className="text-3xl font-bold text-green-400">{stats.bookings}</p>
            <p className="text-slate-400 text-sm">Reservas</p>
          </div>
          <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] text-center">
            <p className="text-3xl font-bold text-purple-400">{stats.conversionRate}</p>
            <p className="text-slate-400 text-sm">Conversión</p>
          </div>
        </div>
      )}

      {/* Visibility Options */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="text-white font-semibold mb-4">Opciones de Visibilidad</h3>
        
        <div className="space-y-3">
          {[
            { id: 'public', label: 'Público', desc: 'Cualquiera puede ver y buscar tu propiedad', icon: Eye },
            { id: 'unlisted', label: 'Sin listar', desc: 'Solo accesible con el enlace directo', icon: EyeOff },
            { id: 'private', label: 'Privado', desc: 'Requiere aprobación para ver', icon: Lock },
          ].map((option) => (
            <label
              key={option.id}
              className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition ${
                settings.visibility === option.id 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : 'border-[var(--v-surface-3)] hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="visibility"
                value={option.id}
                checked={settings.visibility === option.id}
                onChange={(e) => setSettings({...settings, visibility: e.target.value as any})}
                className="w-4 h-4 text-blue-600"
              />
              <option.icon className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-white font-medium">{option.label}</p>
                <p className="text-slate-400 text-sm">{option.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Booking Options */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="text-white font-semibold mb-4">Opciones de Reserva</h3>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-white">Permitir reservas directas</p>
                <p className="text-slate-400 text-sm">Huéspedes pueden reservar sin contactarte primero</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.allowDirectBooking}
              onChange={(e) => setSettings({...settings, allowDirectBooking: e.target.checked})}
              className="w-5 h-5 rounded border-slate-500 text-blue-600"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-white">Mostrar disponibilidad</p>
                <p className="text-slate-400 text-sm">Calendario visible en la página pública</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.showAvailability}
              onChange={(e) => setSettings({...settings, showAvailability: e.target.checked})}
              className="w-5 h-5 rounded border-slate-500 text-blue-600"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-white">Requerir cuenta Vecindus</p>
                <p className="text-slate-400 text-sm">Solo usuarios registrados pueden ver</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.requireLogin}
              onChange={(e) => setSettings({...settings, requireLogin: e.target.checked})}
              className="w-5 h-5 rounded border-slate-500 text-blue-600"
            />
          </label>
        </div>
      </div>

      {/* Custom URL */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <label className="block text-slate-400 text-sm mb-2">URL personalizada</label>
        <div className="flex items-center gap-2">
          <span className="text-slate-500">vecindus.com/p/</span>
          <input
            type="text"
            value={settings.customSlug}
            onChange={(e) => setSettings({...settings, customSlug: e.target.value})}
            className="flex-1 bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600"
          />
        </div>
      </div>
    </div>
  );
}
